import { animate } from "motion";

// Enhanced icon animations using Motion.dev
class MotionIcons {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.setupAnimations(),
      );
    } else {
      this.setupAnimations();
    }

    // Also setup animations when Turbo navigates (for SPA-like behavior)
    document.addEventListener("turbo:load", () => {
      this.setupAnimations();
    });
  }

  setupAnimations() {
    this.setupDeleteAnimation();
    this.setupEditAnimation();
    this.setupMessageAnimation();
    this.setupAddAnimation();
    this.setupTelescopeAnimation();
    this.setupRobotAnimation();
    this.setupShakeAnimation();
    this.setupTwistAnimation();
    this.setupPopAnimation();
    this.setupExplodeAnimation();
    this.setupSpinAnimation();
    this.setupLoadingAnimation();
    this.setupPulseAnimation();
    this.setupMoveForwardAnimation();
    this.setupMoveBackAnimation();
    this.setupExpandAnimation();
  }

  setupDeleteAnimation() {
    const deleteElements = document.querySelectorAll(".delete-animation");

    deleteElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Simple nodding animation using keyframes
        animate(
          element,
          {
            y: [0, -4, 4, 0],
            rotate: [0, 3, -3, 0],
          },
          {
            duration: 0.6,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupEditAnimation() {
    const editElements = document.querySelectorAll(".edit-animation");

    editElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Simple scribble effect using keyframes
        animate(
          element,
          {
            x: [0, 2, -2, 1, -1, 0],
            y: [0, -1, 1, 0, -0.5, 0],
            rotate: [0, 2, -2, 1, -1, 0],
          },
          {
            duration: 0.8,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupMessageAnimation() {
    const messageElements = document.querySelectorAll(".message-animation");

    messageElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Simple ring animation using keyframes
        animate(
          element,
          {
            rotate: [0, -8, 6, -4, 0],
          },
          {
            duration: 0.6,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupAddAnimation() {
    const addElements = document.querySelectorAll(".add-animation");

    addElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Simple bouncy scale animation using keyframes
        animate(
          element,
          {
            scale: [1, 1.2, 0.9, 1],
          },
          {
            duration: 0.6,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupTelescopeAnimation() {
    const telescopeElements = document.querySelectorAll(".telescope-animation");

    telescopeElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        animate(
          element,
          {
            rotate: 20,
          },
          {
            duration: 0.3,
            type: "spring",
            stiffness: 400,
            damping: 25,
          },
        );
      });

      group.addEventListener("mouseleave", () => {
        animate(
          element,
          {
            rotate: 0,
          },
          {
            duration: 0.3,
            type: "spring",
            stiffness: 400,
            damping: 25,
          },
        );
      });
    });
  }

  setupRobotAnimation() {
    const robotElements = document.querySelectorAll(".robot-animation");

    robotElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        animate(
          element,
          {
            skewX: 6,
          },
          {
            duration: 0.3,
            type: "spring",
            stiffness: 400,
            damping: 25,
          },
        );
      });

      group.addEventListener("mouseleave", () => {
        animate(
          element,
          {
            skewX: 0,
          },
          {
            duration: 0.3,
            type: "spring",
            stiffness: 400,
            damping: 25,
          },
        );
      });
    });
  }

  setupShakeAnimation() {
    const shakeElements = document.querySelectorAll(".shake-animation");

    shakeElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Toned down shake animation
        animate(
          element,
          {
            x: [0, -5, 5, -4, 4, -2, 2, -1, 1, 0],
            rotate: [0, -2, 2, -1, 1, -1, 1, 0],
          },
          {
            duration: 0.6,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupTwistAnimation() {
    const twistElements = document.querySelectorAll(".twist-animation");

    twistElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Subtle 60-degree twist animation
        animate(
          element,
          {
            rotate: [0, 30, -30, 60, 0],
            scale: [1, 1.06, 0.94, 1.06, 1],
          },
          {
            duration: 0.8,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupPopAnimation() {
    const popElements = document.querySelectorAll(".pop-animation");

    popElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Toned down pop with bounce
        animate(
          element,
          {
            scale: [1, 1.24, 0.88, 1.12, 1],
            y: [0, -3, 0],
          },
          {
            duration: 0.5,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupExplodeAnimation() {
    const explodeElements = document.querySelectorAll(".explode-animation");

    explodeElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Toned down explosive scale animation
        animate(
          element,
          {
            scale: [1, 1.36, 0.88, 1.12, 1],
          },
          {
            duration: 0.7,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupSpinAnimation() {
    const spinElements = document.querySelectorAll(".spin-animation");

    spinElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Continuous spinning animation
        animate(
          element,
          {
            rotate: [0, 360],
          },
          {
            duration: 1,
            type: "keyframes",
            ease: "linear",
            repeat: Infinity,
          },
        );
      });

      group.addEventListener("mouseleave", () => {
        // Stop spinning and return to original position
        animate(
          element,
          {
            rotate: 0,
          },
          {
            duration: 0.3,
            type: "spring",
            stiffness: 300,
            damping: 20,
          },
        );
      });
    });
  }

  setupLoadingAnimation() {
    const loadingElements = document.querySelectorAll(".loading-animation");

    loadingElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Loading animation with opacity and scale pulsing
        animate(
          element,
          {
            scale: [1, 1.1, 1],
            opacity: [1, 0.7, 1],
          },
          {
            duration: 1.5,
            type: "keyframes",
            ease: "easeInOut",
            repeat: Infinity,
          },
        );
      });

      group.addEventListener("mouseleave", () => {
        // Stop loading animation
        animate(
          element,
          {
            scale: 1,
            opacity: 1,
          },
          {
            duration: 0.3,
            type: "spring",
            stiffness: 300,
            damping: 20,
          },
        );
      });
    });
  }

  setupPulseAnimation() {
    const pulseElements = document.querySelectorAll(".pulse-animation");

    pulseElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Gentle pulsing animation
        animate(
          element,
          {
            scale: [1, 1.15, 1],
          },
          {
            duration: 1,
            type: "keyframes",
            ease: "easeInOut",
            repeat: Infinity,
          },
        );
      });

      group.addEventListener("mouseleave", () => {
        // Stop pulsing
        animate(
          element,
          {
            scale: 1,
          },
          {
            duration: 0.3,
            type: "spring",
            stiffness: 300,
            damping: 20,
          },
        );
      });
    });
  }

  setupMoveForwardAnimation() {
    const moveForwardElements = document.querySelectorAll(
      ".move-forward-animation",
    );

    moveForwardElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Move forward animation - slides right with bounce
        animate(
          element,
          {
            x: [0, 8, 4, 0],
            scale: [1, 1.1, 1],
          },
          {
            duration: 0.6,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupMoveBackAnimation() {
    const moveBackElements = document.querySelectorAll(".move-back-animation");

    moveBackElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Move back animation - slides left with bounce
        animate(
          element,
          {
            x: [0, -8, -4, 0],
            scale: [1, 1.1, 1],
          },
          {
            duration: 0.6,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }

  setupExpandAnimation() {
    const expandElements = document.querySelectorAll(".expand-animation");

    expandElements.forEach((element) => {
      const group = element.closest(".group");
      if (!group) return;

      group.addEventListener("mouseenter", () => {
        // Expand animation with scale and rotation
        animate(
          element,
          {
            scale: [1, 1.3, 1.1, 1.2],
            rotate: [0, 5, -5, 0],
          },
          {
            duration: 0.7,
            type: "keyframes",
            ease: "easeInOut",
          },
        );
      });
    });
  }
}

// Initialize Motion Icons
new MotionIcons();
